import { ArrowRight, Wifi, Wind, Coffee, Tv, ShieldCheck, Car, Users, UtensilsCrossed } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";
import PropertyContact from "@/components/PropertyContact";
import { CONTACTS } from "@/data/contacts";

const IMAGES = {
  hero: "/images/aura/facade.jpg",
  intro: "/images/aura/deluxe_room.jpg",
};

const GALLERY = [
  // Row 1 — Branding (2-up)
  { src: "/images/aura/entrance.jpg",       caption: "Reception & Entrance",        span: "md:col-span-6", aspect: "aspect-[4/3]" },
  { src: "/images/aura/lobby.jpg",          caption: "Lobby & Flavours Restaurant", span: "md:col-span-6", aspect: "aspect-[4/3]" },
  // Row 2 — Hero room (wide) + bathroom amenity
  { src: "/images/aura/superior_deluxe.jpg", caption: "Big Superior Deluxe",        span: "md:col-span-8", aspect: "aspect-[16/10]" },
  { src: "/images/aura/bathroom.jpg",       caption: "En-Suite Bathroom",           span: "md:col-span-4", aspect: "aspect-[16/10]" },
  // Row 3 — 3 room categories (squares)
  { src: "/images/aura/deluxe_room.jpg",    caption: "Deluxe Aura",                 span: "md:col-span-4", aspect: "aspect-square" },
  { src: "/images/aura/club_room.jpg",      caption: "Club Aura",                   span: "md:col-span-4", aspect: "aspect-square" },
  { src: "/images/aura/family_room.jpg",    caption: "Twin-Bed Family",             span: "md:col-span-4", aspect: "aspect-square" },
  // Row 4 — View + transit
  { src: "/images/aura/suite_view.jpg",     caption: "Suite Aura — Mountain View",  span: "md:col-span-7", aspect: "aspect-[4/3]" },
  { src: "/images/aura/elevator.jpg",       caption: "Lift Lobby",                  span: "md:col-span-5", aspect: "aspect-[4/3]" },
  // Row 5 — Conference panoramic
  { src: "/images/aura/conference.jpg",     caption: "Banquet & Conference Hall",   span: "md:col-span-12", aspect: "aspect-[21/9]" },
];

const ROOMS = [
  { name: "Standard Aura", desc: "Well-appointed, fully air-conditioned rooms with everything you need for a comfortable stay." },
  { name: "Deluxe Aura", desc: "More space, mountain views, and enhanced amenities for guests who want a bit extra." },
  { name: "Club Aura", desc: "Our best rooms — premium furnishings, the best views, and the kind of mattresses people keep talking about." },
];

const AMENITIES = [
  { icon: Wifi, label: "High-Speed Wi-Fi" },
  { icon: Wind, label: "Fully Air-Conditioned" },
  { icon: Coffee, label: "In-Room Amenities" },
  { icon: Car, label: "Exclusive Parking" },
  { icon: Users, label: "Banquet & Conference" },
  { icon: UtensilsCrossed, label: "Multi-Cuisine Restaurant" },
];

export default function AmrutAuraPage() {
  return (
    <div data-testid="amrut-aura-page">
      {/* ─── HERO ─── */}
      <section className="relative h-screen w-full overflow-hidden">
        <img src={IMAGES.hero} alt="Amrut Aura room" className="absolute inset-0 w-full h-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 flex flex-col justify-end h-full p-8 md:p-16 lg:p-24 pb-20 md:pb-28">
          <AnimatedSection delay={0.2}><p className="overline-text text-ivory/50 mb-4">Premium Hotel</p></AnimatedSection>
          <AnimatedSection delay={0.4}><h1 className="font-serif text-5xl md:text-6xl lg:text-8xl text-ivory font-light tracking-tighter leading-none mb-6">Amrut Aura</h1></AnimatedSection>
          <AnimatedSection delay={0.6}><p className="text-sm md:text-base text-ivory/60 max-w-lg leading-relaxed">A premium business convenience hotel in the heart of Karwar. Fully air-conditioned, mountain views, and sleep quality that nobody has a complaint about.</p></AnimatedSection>
        </div>
      </section>

      {/* ─── INTRO ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <AnimatedSection>
            <div className="gold-divider mb-8" />
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal font-light tracking-tight leading-tight mb-8">Clean rooms,<br />great sleep,<br />homely atmosphere</h2>
            <p className="text-base text-charcoal/60 leading-relaxed mb-6">
              Amrut Aura is right in the city center — accessible to the bus stand, the market,
              and everything you need. But once you're inside, it's quiet, clean, and comfortable.
              The staff has been with us for years. They know what guests need, often before
              the guests do.
            </p>
            <p className="text-base text-charcoal/60 leading-relaxed">
              We've invested heavily in our mattresses and bedding. The result? Our sleep quality
              is something guests consistently praise. We also have a multi-cuisine restaurant
              (including non-veg), a conference room, and a banquet hall — all under one roof.
            </p>
          </AnimatedSection>
          <AnimatedSection delay={0.2} className="overflow-hidden h-[50vh] md:h-[60vh]">
            <img src={IMAGES.intro} alt="Amrut Aura Deluxe Room" className="w-full h-full object-cover" />
          </AnimatedSection>
        </div>
      </section>

      {/* ─── ROOMS ─── */}
      <section data-testid="aura-rooms" className="py-16 md:py-24 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/50 mb-6">Room Categories</p><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-20">Pick What Suits You</h2></AnimatedSection>
          {ROOMS.map((room, i) => (
            <AnimatedSection key={i} delay={i * 0.1}>
              <div className="border-t border-charcoal/15 py-10 md:py-14 grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 md:gap-12 items-start">
                <h4 className="font-serif text-2xl md:text-3xl text-charcoal font-light tracking-tight">{room.name}</h4>
                <p className="text-sm text-charcoal/60 leading-relaxed">{room.desc}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ─── AMENITIES ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection><p className="overline-text text-charcoal/40 mb-6">What's Included</p><div className="gold-divider mb-16" /></AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-12 md:gap-10">
            {AMENITIES.map((a, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="text-center"><a.icon size={28} strokeWidth={1} className="text-charcoal/40 mx-auto mb-4" /><p className="text-xs text-charcoal/60 tracking-wide">{a.label}</p></div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHO STAYS HERE ─── */}
      <section className="py-12 md:py-16 px-6 md:px-10 lg:px-16 bg-ivory">
        <div className="max-w-[900px] mx-auto text-center">
          <AnimatedSection>
            <p className="overline-text text-charcoal/50 mb-6">Our Guests</p>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal font-light tracking-tight leading-tight mb-8">Business executives, families, navy personnel, exporters</h2>
            <p className="text-sm text-charcoal/60 leading-relaxed max-w-xl mx-auto">Most of our guests are repeat visitors. They come for the cleanliness, stay for the homely atmosphere, and keep coming back because the quality never drops.</p>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── GALLERY ─── */}
      <section data-testid="aura-gallery" className="py-16 md:py-24 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <AnimatedSection>
            <p className="overline-text text-charcoal/40 mb-6">Gallery</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-16">A Look Inside</h2>
          </AnimatedSection>
          <div className="grid grid-cols-12 gap-3 md:gap-4">
            {GALLERY.map((item, i) => (
              <AnimatedSection key={i} delay={Math.min(i * 0.06, 0.36)} className={`col-span-12 ${item.span}`}>
                <figure className={`group relative overflow-hidden ${item.aspect}`}>
                  <img
                    src={item.src}
                    alt={item.caption}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                  />
                  <figcaption className="absolute bottom-0 left-0 right-0 px-5 py-4 bg-gradient-to-t from-charcoal/70 via-charcoal/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <span className="text-[11px] tracking-[0.2em] uppercase text-ivory/90 font-medium">{item.caption}</span>
                  </figcaption>
                </figure>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section className="py-16 md:py-24 px-6 md:px-10 lg:px-16">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection>
            <p className="overline-text text-charcoal/40 mb-6">Find Us</p>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal font-light tracking-tight leading-tight mb-6">City Center, Karwar</h2>
            <p className="text-base text-charcoal/60 leading-relaxed max-w-xl mb-16">Conveniently located and easy to reach — close to the bus stand, railway station, and city market.</p>
          </AnimatedSection>
          <AnimatedSection delay={0.1}><PropertyContact contact={CONTACTS.aura} variant="light" /></AnimatedSection>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-10 md:py-14 px-6 md:px-10 lg:px-16 bg-ivory text-center">
        <div className="max-w-[700px] mx-auto">
          <AnimatedSection><h2 className="font-serif text-4xl md:text-5xl text-charcoal font-light tracking-tight mb-6">Book a Room</h2></AnimatedSection>
          <AnimatedSection delay={0.1}><p className="text-sm text-charcoal/55 leading-relaxed mb-10 max-w-md mx-auto">Call us or send a WhatsApp message. We'll sort out the best room and rate for you.</p></AnimatedSection>
          <AnimatedSection delay={0.2}>
            <a href={`https://wa.me/${CONTACTS.aura.whatsappRaw}?text=I'd%20like%20to%20book%20a%20room%20at%20Amrut%20Aura`} target="_blank" rel="noopener noreferrer" data-testid="book-stay-btn" className="inline-flex items-center gap-3 px-10 py-4 border border-charcoal/25 text-charcoal text-xs tracking-[0.2em] uppercase font-medium hover:bg-charcoal hover:text-ivory transition-all duration-500">
              Book Your Stay <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
